/**
 * Tool Display Configuration
 *
 * Maps technical tool names to human-readable labels and formatters
 * for the tool call visibility UI.
 */

import { ToolCallCategory } from './type';

interface ToolDisplayConfigEntry {
  label: string;
  activeLabel: string;
  icon: string;
  category: ToolCallCategory;
  resultFormatter: (data: unknown) => string;
}

export const TOOL_DISPLAY_CONFIG: Record<string, ToolDisplayConfigEntry> = {
  // Risk Assessment Tools
  find_risk_summary: {
    label: 'Risk Summary',
    activeLabel: 'Analyzing risk summary',
    icon: '\u26A0\uFE0F',
    category: 'risk',
    resultFormatter: (data) => {
      const d = data as { totals?: { at_risk_count?: number; total_skus?: number } };
      return `${d.totals?.at_risk_count ?? 0} at-risk of ${d.totals?.total_skus ?? 0} SKUs`;
    },
  },

  find_at_risk_skus: {
    label: 'At-Risk SKUs',
    activeLabel: 'Finding at-risk SKUs',
    icon: '\uD83D\uDD0D',
    category: 'risk',
    resultFormatter: (data) => {
      const d = data as {
        summary?: {
          at_risk_count?: number;
          severity_breakdown?: {
            critical?: number;
            warning?: number;
            info?: number;
          };
        };
        at_risk_skus?: unknown[];
      };
      const breakdown = d.summary?.severity_breakdown;
      if (breakdown) {
        const parts = [];
        if (breakdown.critical) parts.push(`${breakdown.critical} critical`);
        if (breakdown.warning) parts.push(`${breakdown.warning} warning`);
        if (breakdown.info) parts.push(`${breakdown.info} info`);
        if (parts.length > 0) return parts.join(', ');
      }
      return `${d.summary?.at_risk_count ?? d.at_risk_skus?.length ?? 0} SKUs identified`;
    },
  },

  find_stockout_date: {
    label: 'Stockout Date',
    activeLabel: 'Finding stockout date',
    icon: '\uD83D\uDCC5',
    category: 'risk',
    resultFormatter: (data) => {
      const d = data as { has_stockout?: boolean; stockout_date?: string; days_until_stockout?: number; scenario?: string };
      const scenarioLabel = d.scenario ? `Scenario=${d.scenario} \u00B7 ` : '';
      if (!d.has_stockout) return `${scenarioLabel}No stockout projected`;
      return `${scenarioLabel}Stockout in ${d.days_until_stockout ?? '?'} days`;
    },
  },

  // Supply Analysis Tools
  get_supply_walk_for_sku: {
    label: 'Supply Walk',
    activeLabel: 'Loading supply walk',
    icon: '\uD83D\uDCCA',
    category: 'supply',
    resultFormatter: (data) => {
      const d = data as { summary?: { total_periods?: number }; data?: unknown[] };
      return `${d.summary?.total_periods ?? d.data?.length ?? 0} periods loaded`;
    },
  },

  find_low_point: {
    label: 'Low Point',
    activeLabel: 'Finding inventory low point',
    icon: '\uD83D\uDCC9',
    category: 'supply',
    resultFormatter: (data) => {
      const d = data as { low_point?: { ending_inventory?: number; date?: string } };
      return `${d.low_point?.ending_inventory ?? 0} units on ${d.low_point?.date ?? 'N/A'}`;
    },
  },

  // History Tools
  get_po_history_for_sku: {
    label: 'PO History',
    activeLabel: 'Getting PO history',
    icon: '\uD83D\uDCCB',
    category: 'history',
    resultFormatter: (data) => {
      const d = data as { summary?: { total_pos?: number }; purchase_orders?: unknown[] };
      const count = d.summary?.total_pos ?? d.purchase_orders?.length ?? 0;
      return `${count} purchase order${count !== 1 ? 's' : ''}`;
    },
  },

  get_po_coverage_for_sku: {
    label: 'PO Coverage',
    activeLabel: 'Analyzing PO coverage',
    icon: '\uD83E\uDDEE',
    category: 'supply',
    resultFormatter: (data) => {
      const d = data as {
        purchase_orders?: unknown[];
        coverage_summary?: {
          has_real_po_before_stockout?: boolean;
          coverage_gap_ordered_only?: number;
          delivery_data_available?: boolean;
          has_simulated_recommendation?: boolean;
          simulated_po_arrives_before_stockout?: boolean;
          has_reconciliation_ambiguity?: boolean;
          po_match_confidence_counts?: {
            exact?: number;
            aggregated?: number;
            unmatched?: number;
          };
        };
      };
      const gap = d.coverage_summary?.coverage_gap_ordered_only;
      if (d.coverage_summary?.has_real_po_before_stockout === false) {
        if (
          d.coverage_summary?.has_simulated_recommendation &&
          d.coverage_summary?.simulated_po_arrives_before_stockout === false
        ) {
          return 'No real PO before stockout; recommendation arrives too late';
        }
        return 'No Ordered/Delivered PO before stockout';
      }
      if (typeof gap === 'number' && gap > 0) {
        return `Ordered coverage gap: ${gap} units`;
      }
      if (d.coverage_summary?.has_reconciliation_ambiguity) {
        const counts = d.coverage_summary?.po_match_confidence_counts;
        return `Coverage reconciled with ambiguity (exact ${counts?.exact ?? 0}, aggregated ${counts?.aggregated ?? 0}, unmatched ${counts?.unmatched ?? 0})`;
      }
      if (d.coverage_summary?.delivery_data_available === false) {
        return 'Coverage complete (ETA-based late signal)';
      }
      return `${d.purchase_orders?.length ?? 0} POs reconciled`;
    },
  },

  get_sku_master_attributes: {
    label: 'SKU Master',
    activeLabel: 'Loading SKU master attributes',
    icon: '\uD83E\uDDFE',
    category: 'supply',
    resultFormatter: (data) => {
      const d = data as {
        is_purchasable?: boolean;
        is_conditionally_purchasable?: boolean;
        purchasing_eligibility?: string;
        purchasing_scope?: { scope_dimension?: string; exclusion_scope?: string };
        locations_evaluated?: string[];
        blockers?: Array<{ code?: string }>;
        lead_time_days?: number;
        supplier_assigned?: boolean;
        supplier_is_ambiguous?: boolean;
      };
      if (Array.isArray(d.locations_evaluated) && d.locations_evaluated.length > 1) {
        return `Evaluated ${d.locations_evaluated.length} locations`;
      }
      if (d.purchasing_eligibility === 'conditional' || d.is_conditionally_purchasable) {
        const scope =
          d.purchasing_scope?.scope_dimension ??
          (d.purchasing_scope?.exclusion_scope === 'mixed' ? 'scope' : 'channel');
        return `Conditional purchasability (${scope}-specific rules)`;
      }
      if (d.supplier_is_ambiguous) {
        return 'Master loaded (supplier mapping is ambiguous)';
      }
      if (d.is_purchasable) {
        return 'Purchasable (no master-data blockers)';
      }
      const blocker = d.blockers?.[0]?.code;
      if (blocker) {
        return `Blocked: ${blocker}`;
      }
      if (d.supplier_assigned === false) {
        return 'Blocked: no supplier assigned';
      }
      if (typeof d.lead_time_days !== 'number') {
        return 'Blocked: lead time missing';
      }
      return 'Master attributes loaded';
    },
  },

  get_actual_demand_for_sku: {
    label: 'Actual Demand',
    activeLabel: 'Comparing demand vs forecast',
    icon: '\uD83D\uDCC8',
    category: 'comparison',
    resultFormatter: (data) => {
      const d = data as {
        anomaly_flags?: {
          major_demand_anomaly?: boolean;
          anomaly_reason?: string;
        };
        single_run_context?: {
          demand_running_hot?: boolean;
          demand_exceeded_forecast?: boolean;
          excess_pct?: number;
          confidence_level?: string;
        };
        cross_run_comparison?: { demand_exceeded_forecast?: boolean; forecast_miss_pct?: number; overlap_days?: number };
      };
      if (d.anomaly_flags?.major_demand_anomaly) {
        return `Major demand anomaly: ${d.anomaly_flags?.anomaly_reason ?? 'extreme actual-vs-forecast divergence'}`;
      }
      const cross = d.cross_run_comparison;
      if (cross?.demand_exceeded_forecast) {
        const miss = cross.forecast_miss_pct ?? 0;
        return `Cross-run miss: +${miss.toFixed(1)}% (${cross.overlap_days ?? 0} days)`;
      }
      if (d.single_run_context?.demand_exceeded_forecast) {
        const excess = d.single_run_context?.excess_pct ?? 0;
        const confidence = d.single_run_context?.confidence_level ?? 'medium';
        return `Single-run demand above forecast: +${excess.toFixed(1)}% (${confidence} confidence)`;
      }
      if (d.single_run_context?.demand_running_hot) {
        return 'Demand running hot (context only)';
      }
      return 'Demand signal reviewed';
    },
  },

  get_inventory_position: {
    label: 'Inventory Position',
    activeLabel: 'Checking inventory position',
    icon: '\uD83D\uDCE6',
    category: 'supply',
    resultFormatter: (data) => {
      const d = data as {
        discrepancy_pct?: number;
        is_significant?: boolean;
        system_overestimates?: boolean;
      };
      const pct = d.discrepancy_pct ?? 0;
      if (d.is_significant && d.system_overestimates) {
        return `Significant overestimate: ${pct.toFixed(1)}%`;
      }
      if (d.is_significant) {
        return `Significant discrepancy: ${pct.toFixed(1)}%`;
      }
      return `Aligned (${pct.toFixed(1)}% discrepancy)`;
    },
  },

  get_supplier_delivery_stats: {
    label: 'Supplier Delivery',
    activeLabel: 'Analyzing supplier delivery',
    icon: '\uD83D\uDE9A',
    category: 'history',
    resultFormatter: (data) => {
      const d = data as { supplier?: string; on_time_rate?: number; delivered_pos?: number };
      const supplier = d.supplier ?? 'Supplier';
      return `${supplier}: ${d.on_time_rate ?? 0}% on-time (${d.delivered_pos ?? 0} delivered)`;
    },
  },

  // Comparison Tools
  compare_supply_walks: {
    label: 'Supply Walk Comparison',
    activeLabel: 'Comparing supply walks',
    icon: '\uD83D\uDD04',
    category: 'comparison',
    resultFormatter: (data) => {
      const d = data as { root_cause_indicator?: { primary_variable?: string; confidence?: string } };
      const cause = d.root_cause_indicator?.primary_variable ?? 'UNKNOWN';
      const conf = d.root_cause_indicator?.confidence ?? 'low';
      return `Primary: ${cause} (${conf} confidence)`;
    },
  },

  diagnose_shortfall: {
    label: 'Shortfall Diagnosis',
    activeLabel: 'Diagnosing shortfall',
    icon: '\uD83D\uDD2C',
    category: 'comparison',
    resultFormatter: (data) => {
      const d = data as {
        diagnosis?: {
          risk_type?: string;
          days_until_risk?: number;
          classification?: string;
          confidence?: string;
          primary_location?: string;
        };
        locations_evaluated?: string[];
        root_cause?: { primary_variable?: string; confidence?: string };
      };
      if (Array.isArray(d.locations_evaluated) && d.locations_evaluated.length > 1) {
        const primary = d.diagnosis?.primary_location;
        return `Evaluated ${d.locations_evaluated.length} locations${primary ? ` · Primary ${primary}` : ''}`;
      }
      const risk = d.diagnosis?.risk_type?.toUpperCase() ?? 'UNKNOWN';
      const days = d.diagnosis?.days_until_risk;
      const classification = d.diagnosis?.classification ?? d.root_cause?.primary_variable ?? 'UNKNOWN';
      const confidence = d.diagnosis?.confidence ?? d.root_cause?.confidence ?? 'low';
      return `${risk} in ${days ?? '?'} days \u00B7 ${classification} (${confidence})`;
    },
  },

  // Transform Tools
  apply_transform: {
    label: 'Data Transform',
    activeLabel: 'Applying transform',
    icon: '\u26A1',
    category: 'transform',
    resultFormatter: (data) => {
      const d = data as { row_count?: number };
      return `${d.row_count ?? 0} rows`;
    },
  },

  // List Tools
  list_runs: {
    label: 'Run List',
    activeLabel: 'Listing runs',
    icon: '\uD83D\uDCD1',
    category: 'history',
    resultFormatter: (data) => {
      const d = data as { runs?: unknown[] };
      return `${d.runs?.length ?? 0} runs found`;
    },
  },
};

// Category colors for visual grouping
export const CATEGORY_COLORS: Record<ToolCallCategory, { bg: string; border: string; icon: string }> = {
  risk: { bg: '#fef3c7', border: '#f59e0b', icon: '#d97706' },      // Amber
  supply: { bg: '#dbeafe', border: '#3b82f6', icon: '#2563eb' },    // Blue
  comparison: { bg: '#f3e8ff', border: '#a855f7', icon: '#9333ea' }, // Purple
  history: { bg: '#fce7f3', border: '#ec4899', icon: '#db2777' },   // Pink
  transform: { bg: '#d1fae5', border: '#10b981', icon: '#059669' }, // Green
};
